/*
  <shintop-card-stepper> — the card Add button that morphs into a − qty + stepper.

  OWNS ITS QUEUE (audit 2026-10-14, R04-3 · S04-004). Until 2026-10-18 this extended Dawn's
  BulkAdd (assets/global.js) for the pending queue and the single-in-flight drain, and overrode
  its updateMultipleQty. That leaned on stock internals Dawn had already restructured once
  (15.3.0), and the borrowed queue was the root of one family of quantity bugs — S03-002,
  S03-018, S03-019, H03-01, H03-08: BulkAdd empties its queue the moment a request fires, so
  nothing here knew which number was the server's and which was a guess. The queue is now this
  element's own — ONE slot for the newest absolute quantity not yet sent, ONE request on the
  wire — and every write still passes through `capRise`, the floor rule.

  Each write is ONE POST to Dawn's routes.cart_update_url with an ABSOLUTE quantity (idempotent;
  last write wins), sections limited to the cart's own (the drawer and the badge —
  assets/shintop-cart.js says which). Painting a whole grid section per tap was deliberately
  rejected: it would destroy focus mid-interaction and repaint every card image; the response's
  cart JSON is the same truth, painted locally.

  Sync contract: the after-write helper (assets/shintop-cart.js) publishes
  PUB_SUB_EVENTS.cartUpdate with { source: 'shintop-card-stepper', instanceId, cartData }, and
  this element subscribes to the same event skipping ONLY its own instanceId — a sibling stepper
  showing the same variant in another section must repaint, so skipping by source string would
  desync it.

  Accessibility (research §4, normative): focus moves to + on morph and back to Add on
  collapse (never to body); the input auto-selects on focus; announcements coalesce
  into one polite status; the Add button uses aria-disabled + a click guard in flight,
  never the bare disabled attribute.

  Feedback is the morph itself — no cart notification is opened (NN/g: persistent,
  not transient). The badge updates silently via the bundled section render.
*/
(() => {
  if (customElements.get('shintop-card-stepper')) return;

  const DEBOUNCE_MS = 300;
  const ANNOUNCE_MS = 600;

  // One counter orders cart reads against write answers (H03-01, in syncFromServer).
  let clock = 0;
  const tick = () => (clock += 1);

  /* One shared cart fetch per event burst, however many steppers are on the page. A caller that
     needs a read started after a given tick (its own last write's answer) gets a fresh one. */
  let sharedCartPromise = null;
  const fetchCart = (after = 0) => {
    if (!sharedCartPromise || sharedCartPromise.startedAt <= after) {
      const startedAt = tick();
      const read = fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .finally(() => {
          setTimeout(() => {
            if (sharedCartPromise === read) sharedCartPromise = null;
          }, 100);
        });
      read.startedAt = startedAt;
      sharedCartPromise = read;
    }
    return sharedCartPromise;
  };

  customElements.define(
    'shintop-card-stepper',
    class ShintopCardStepper extends HTMLElement {
      constructor() {
        super();
        /* The queue (R04-3). `pendingQty` is the newest absolute quantity not yet sent and
           `inFlight` the one on the wire — each a number or null, because 0 (remove) is a real
           write. `settledAt` is the tick at which this element's last write was answered,
           either way. */
        this.pendingQty = null;
        this.inFlight = null;
        this.settledAt = 0;
      }

      connectedCallback() {
        this.instanceId = `cstep-${Math.random().toString(36).slice(2)}`;
        this.variantId = this.dataset.variantId;
        this.max = parseInt(this.dataset.max, 10) || 99;

        this.addButton = this.querySelector('.shintop-cstep__add:not([disabled])');
        this.form = this.querySelector('.shintop-cstep__form');
        this.stepper = this.querySelector('.shintop-cstep__stepper');
        this.input = this.querySelector('.shintop-cstep__input');
        this.minusBtn = this.querySelector('.shintop-cstep__btn--minus');
        this.plusBtn = this.querySelector('.shintop-cstep__btn--plus');
        this.errorEl = this.querySelector('.shintop-cstep__error');
        this.statusEl = this.querySelector('.shintop-cstep__status');
        this.dealEl = this.querySelector('[data-deal-line]');
        this.dealMin = parseInt(this.dataset.dealMin, 10) || 0;
        if (!this.addButton || !this.stepper) return; // sold-out card: static

        this.serverQty = parseInt(this.input.value, 10) || 0;
        this.lineKey = null;

        this.form.addEventListener('submit', (event) => this.onAddClick(event));
        this.minusBtn.addEventListener('click', () => this.step(-1));
        this.plusBtn.addEventListener('click', () => this.step(1));
        this.input.addEventListener('focus', () => this.input.select());
        this.input.addEventListener('input', () => this.onTyped());
        this.input.addEventListener('blur', () => this.onBlur());
        this.input.addEventListener('keydown', (event) => this.onKeydown(event));

        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onForeignCartUpdate(event));

        // bfcache restore shows a snapshot of an older cart; re-sync from truth.
        this.onPageShow = (event) => {
          if (event.persisted) this.syncFromServer();
        };
        window.addEventListener('pageshow', this.onPageShow);

        /*
          S03-018 (audit 2026-10-14). A tab that comes back to the front may be showing a number
          another tab has since changed, and every write here is absolute: "+" on a stale 3
          overwrote the other tab's 5 with 4. Coming back is treated exactly like a mount after
          load (below) — unverified, one shared re-read, and a tap in the meantime is held.
        */
        this.onVisibility = () => {
          if (document.visibilityState !== 'visible') return;
          this.unverified = true;
          this.syncFromServer();
        };
        document.addEventListener('visibilitychange', this.onVisibility);

        /*
          A MOUNT AFTER PAGE LOAD IS A PAINT, NOT TRUTH (audit 2026-09-13, S03-04, P0).

          `FacetFiltersForm.filterData` (assets/facets.js:114-125) is an unbounded cache of
          rendered grid HTML with no invalidation path — not on a cart change, not on a timer.
          This card carries cart state INSIDE that cached HTML, so re-ticking a filter, or
          pressing Back, repaints 24 steppers with whatever the cart held when the grid was
          first rendered. `step()` then computes from the DISPLAYED number and the write sends
          an ABSOLUTE quantity: measured live, a cart line of 3 became 1 on one tap, silently,
          with the header badge agreeing with the loss.

          So whenever we were created after the document finished parsing, the server-rendered
          number is not evidence about the cart and we re-read it. `fetchCart()` shares one
          promise per burst, so a 24-card repaint costs ONE /cart.js. `paintFromCart` already
          yields to an in-flight write, a queued intent and a pending step, so this cannot
          eat a tap that is mid-flight.
        */
        if (document.readyState !== 'loading') {
          this.unverified = true;
          this.syncFromServer();
        }
      }

      disconnectedCallback() {
        this.unsubscribe?.();
        window.removeEventListener('pageshow', this.onPageShow);
        document.removeEventListener('visibilitychange', this.onVisibility);
      }

      get uiQty() {
        return parseInt(this.input.value, 10) || 0;
      }

      /*
        S03-04, SECOND HALF (Naoya on the draft, 2026-09-17: "it would try to show the add to
        cart badge for a second, and it looks like forcing it to be quantity one").

        Re-reading the cart at mount fixes the DISPLAY, but it is a network round trip —
        measured at 338 ms on the draft — and until it lands the stale control is still live.
        Every write this element makes is an ABSOLUTE quantity computed from the number on
        screen (`onAddClick` -> 1, `step` -> uiQty + delta, `commitTyped` -> what was typed), so
        a tap inside that window still collapses the line: measured, a tap 12 ms in took a real
        cart of 3 down to 1. Narrowing a P0 is not closing it.

        DISABLING THE CONTROL WOULD BE WORSE THAN THE BUG. The window opens on every post-load
        mount, and in the ordinary case the cached number is perfectly correct — so a guard that
        swallowed the tap would silently eat the first press of every shopper whose cart really
        is empty, which is most of them. The tap is therefore HELD, not dropped: the intent is
        remembered as a delta, and applied to the server's number once it arrives.

        S03-019 (audit 2026-10-14): the held intent was ONE slot, so "+ + +" inside the window
        landed as a single +1 — the last tap, not the burst. Steps now ACCUMULATE (deltas add; a
        typed number replaces what was held, and a step after it moves that number), and each
        held step is painted at once so the shopper sees the burst they made. The sum is applied
        to the server's number through `step`, so it still passes `capRise` — and if no number
        arrives, to the one the card showed before the burst (`heldFrom`), never to its own paint.
      */
      holdUntilVerified(intent) {
        if (!this.unverified) return false;
        const held = this.pendingIntent;
        if (this.heldFrom == null) this.heldFrom = this.uiQty;
        if (intent.type === 'step') {
          const shown = this.capRise(this.uiQty, this.uiQty + intent.delta);
          if (shown > 0) {
            this.input.value = shown;
            this.paintDeal(shown);
          }
          if (held?.type === 'step') intent = { type: 'step', delta: held.delta + intent.delta };
          if (held?.type === 'typed') intent = { type: 'typed', value: String((parseInt(held.value, 10) || 0) + intent.delta) };
        }
        this.pendingIntent = intent;
        this.syncFromServer();
        return true;
      }

      applyPendingIntent(intent) {
        // paintFromCart has just written server truth into the input, so uiQty is now truth.
        if (intent.type === 'step') return this.step(intent.delta);
        if (intent.type === 'typed') {
          this.input.value = intent.value;
          return this.commitTyped();
        }
        // 'add' = "I want one of these". If the server says some are already in the cart the
        // card was lying and they have them — show that and write nothing, rather than adding a
        // unit they did not ask for. Only a genuinely empty line is actually added to.
        if (this.serverQty === 0) this.onAddClick();
      }

      onAddClick(event) {
        event?.preventDefault(); // no-JS fallback is the real POST; with JS we take over
        if (this.addButton.getAttribute('aria-disabled') === 'true') return;
        if (this.holdUntilVerified({ type: 'add' })) return;
        this.cancelPendingStep(); // a stale armed step must never outlive a fresh add (ORCH-2)
        this.addButton.setAttribute('aria-disabled', 'true');
        this.showStepper(1, { focus: this.plusBtn });
        this.enqueue(1);
      }

      /*
        THE CAP LIMITS A RISE AND NEVER LOWERS A LINE (audit 2026-10-14, S03-002, P2).

        `max` is at most 10 (the snippet bounds it so the HTML never publishes the stock count),
        but nothing stops the cart holding more — the product page has no such cap. Clamping
        every move to `max` made "+" on a line of 12 write an absolute 10, and so did tabbing
        through the box without typing, because the blur commit clamped 12 too: the control
        meant to stop a rise to a quantity that must fail was LOWERING a line the server had
        already accepted. So a fall is never capped, and a capped rise stops where it started.

        The one floor every write path goes through — `step()`, `commitTyped()` and a held
        step's paint; the queue below sends only what they computed.
      */
      capRise(from, to) {
        if (to <= from) return Math.max(to, 0);
        return Math.max(from, Math.min(to, this.max));
      }

      step(delta) {
        if (this.holdUntilVerified({ type: 'step', delta })) return;
        const next = this.capRise(this.uiQty, this.uiQty + delta);
        if (next === this.uiQty && delta > 0) {
          this.announceSoon();
          return; // at the honest cap
        }
        if (next === 0) {
          this.showButton({ focus: true });
        } else {
          this.input.value = next;
          this.paintDeal(next);
        }
        // Trailing 250ms coalesce: the number already updated optimistically, so a
        // +/+/+ burst paints instantly but lands as one absolute request, not eight.
        // stepPending covers the window before enqueue fills the queue's slot:
        // without it an in-flight response's paintFromCart reverts the optimistic
        // input and EATS the click (ORCH-1, round 1 — reproduced live: add + two
        // rapid +'s ended at server qty 2).
        clearTimeout(this.stepTimer);
        this.stepPending = true;
        this.stepTimer = setTimeout(() => {
          this.stepPending = false;
          this.enqueue(next);
        }, 250);
      }

      onTyped() {
        clearTimeout(this.typeTimer);
        this.typeTimer = setTimeout(() => {
          const raw = this.input.value.trim();
          if (raw === '') return; // wait for blur to resolve emptiness
          this.commitTyped();
        }, DEBOUNCE_MS);
      }

      onBlur() {
        clearTimeout(this.typeTimer);
        this.commitTyped();
      }

      /*
        S06-014 (audit 2026-10-14). Enter used to call blur() to borrow the blur commit, and that
        threw focus to <body> — on a 0 it also skipped the Add button commitTyped hands focus to,
        because by then the focus was no longer ours. Enter now commits in place: focus stays in
        the box with the number selected for the next edit, and a 0 lands on Add.
      */
      onKeydown(event) {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        clearTimeout(this.typeTimer);
        this.commitTyped();
        if (document.activeElement === this.input) this.input.select();
      }

      /*
        Both fresher-intent entry points cancel any armed step coalesce first
        (ORCH-2, round 2): an armed stepTimer would otherwise re-enqueue its stale
        absolute quantity AFTER the fresh typed commit / re-add (last write wins,
        silently overwriting the shopper's real intent), and an armed stepPending
        would suppress the fresh action's own repaint.
      */
      cancelPendingStep() {
        clearTimeout(this.stepTimer);
        this.stepPending = false;
      }

      commitTyped() {
        if (this.holdUntilVerified({ type: 'typed', value: this.input.value })) return;
        this.cancelPendingStep(); // typed intent supersedes an armed step (ORCH-2)
        let next = parseInt(this.input.value, 10);
        if (Number.isNaN(next) || next < 0) next = this.serverQty; // garbage → server truth
        next = this.capRise(this.serverQty, next);
        if (next === 0) {
          // Restore focus only if it is still ours — a typed 0 must land on the Add
          // button (never body), but a blur-commit means the shopper already left.
          const stillOurs = this.contains(document.activeElement);
          this.showButton({ focus: stillOurs });
        } else {
          this.input.value = next;
          this.paintDeal(next);
        }
        if (next !== this.serverQty) this.enqueue(next);
      }

      /* ---------- the queue (R04-3): one slot, one request in flight ---------- */

      enqueue(quantity) {
        this.hideError();
        // The newest intent replaces an older one still waiting; one equal to the write already on
        // the wire has nothing left to say.
        this.pendingQty = quantity === this.inFlight ? null : quantity;
        this.drain();
      }

      drain() {
        if (this.inFlight !== null || this.pendingQty === null) return;
        const quantity = this.pendingQty;
        this.pendingQty = null;
        this.write(quantity);
      }

      write(quantity) {
        this.inFlight = quantity;
        const body = JSON.stringify({
          updates: { [this.variantId]: quantity },
          sections: window.shintopCart ? window.shintopCart.sections() : ['cart-icon-bubble'],
          sections_url: window.location.pathname,
        });

        fetch(routes.cart_update_url, { ...fetchConfig(), body })
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.errors || parsedState.status === 422) {
              throw Object.assign(new Error(parsedState.description || parsedState.errors), { code: 'INVALID' });
            }
            this.settledAt = tick();
            this.paintFromCart(parsedState, { announce: true, own: true });
            /* The drawer, the badge and the publish are the shared after-write step. It never
               opens anything for this element, and nothing it does can reach the catch below:
               the write has landed, and an error shown for an action that WORKED is the defect
               audit 2026-09-13 S03-02 found here. Without it (the helper failed to load) the
               badge still repaints and the page still hears about the write. */
            if (window.shintopCart) {
              window.shintopCart.afterCartWrite(parsedState, {
                source: 'shintop-card-stepper',
                instanceId: this.instanceId,
              });
            } else {
              this.renderBadge(parsedState);
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'shintop-card-stepper',
                instanceId: this.instanceId,
                cartData: parsedState,
              });
            }
          })
          .catch((error) => {
            this.settledAt = tick();
            this.writeFailed(error);
          })
          .finally(() => {
            this.inFlight = null;
            this.addButton.removeAttribute('aria-disabled');
            this.drain();
            // A re-read that was answered while this write was out was dropped (H03-01); read again.
            if (this.unverified && this.inFlight === null) this.syncFromServer();
          });
      }

      /*
        H03-08 (audit 2026-10-14). When a write fails, the number on screen is a guess the server
        never confirmed — and it used to stay painted and become the base of the next absolute
        write, so a failed "+" followed by a working one added two. Everything derived from it
        goes: the armed step and the waiting quantity are dropped, the last server-verified number
        is painted back, and the card is unverified again, so a tap before the re-read lands is
        held and applied to truth (or, if the re-read fails too, to that verified number — never
        to the guess).
      */
      writeFailed(error) {
        this.cancelPendingStep();
        this.pendingQty = null;
        this.paintQty(this.serverQty);
        this.showError(error.code === 'INVALID' ? error.message : window.cartStrings?.error);
        this.unverified = true;
      }

      /* ---------- painting ---------- */

      qtyFromCart(cart) {
        const line = (cart?.items || []).find((item) => String(item.variant_id) === String(this.variantId));
        this.lineKey = line?.key || this.lineKey;
        return line ? line.quantity : 0;
      }

      /*
        announce is opt-in and LOCAL-only: round 1's technical pass caught every card
        on the page announcing "removed from cart" whenever any other card mutated —
        foreign repaints and server syncs stay silent.
      */
      paintFromCart(cart, { announce = false, own = false } = {}) {
        /*
          ORCH-3 (Naoya, 2026-08-13: "when i click 2 products almost at the same time…
          sometimes it goes back to add to cart button after the quantity input UI").

          A cart snapshot published by ANOTHER stepper was taken before our own write
          landed, so it shows this variant at 0 — and adopting it collapses the stepper
          back to Add, silently losing the shopper's click. The queue's slot is already
          empty once the write is on the wire, and stepPending is false by then, so
          neither of those guards covers the in-flight window.

          Only our own response knows the truth about our own write, so it passes
          own:true and always paints; every foreign snapshot waits. serverQty is left
          alone too — our response sets it authoritatively a moment later, and adopting
          a stale value here would make a following typed commit look like a no-op.
        */
        if (!own && this.inFlight !== null) return;
        this.serverQty = this.qtyFromCart(cart);
        // A newer intent is queued (or a step's trailing coalesce hasn't enqueued
        // yet — ORCH-1); let the next write's response paint instead.
        if (this.pendingQty !== null || this.stepPending) return;
        this.paintQty(this.serverQty);
        if (announce) this.announceSoon();
      }

      /*
        S06-015 (audit 2026-10-14), and its mirror found fixing it (S11-08). A repaint that swaps
        the control under the shopper's focus used to drop that focus to <body>: a re-sync to 0
        hid the − / box / + they were on, and a snapshot that put the line in the cart hid the Add
        button they were on. Focus follows the control — Add on a collapse, + on a morph — and only
        when it was ours; a repaint never pulls focus in from elsewhere on the page.
      */
      paintQty(qty) {
        const focused = this.contains(document.activeElement);
        if (qty === 0) {
          if (this.dataset.state !== 'button') this.showButton({ focus: focused });
        } else if (this.dataset.state !== 'stepper') {
          this.showStepper(qty, { focus: focused ? this.plusBtn : null });
        } else if (document.activeElement !== this.input) {
          this.input.value = qty;
          this.paintDeal(qty);
        }
      }

      showStepper(qty, { focus } = {}) {
        this.dataset.state = 'stepper';
        this.input.value = qty;
        this.addButton.hidden = true;
        this.form.hidden = true; // the ghost-form fix: the empty form must leave flow too
        this.stepper.hidden = false;
        if (focus) focus.focus();
        this.paintDeal(qty);
      }

      showButton({ focus } = {}) {
        this.dataset.state = 'button';
        this.stepper.hidden = true;
        this.form.hidden = false;
        this.addButton.hidden = false;
        if (focus) this.addButton.focus(); // never let focus fall to body
        this.paintDeal(0);
      }

      /*
        The deal line walks rest → goal-gradient → applied with the quantity the
        shopper SEES (optimistic paints included). Strings arrive Liquid-interpolated
        per card — percent/count wording only, never a JS-fabricated money figure.
      */
      paintDeal(qty) {
        if (!this.dealEl || !this.dealMin) return;
        if (qty >= this.dealMin) {
          this.dealEl.textContent = this.dataset.strDealApplied;
        } else if (qty > 0) {
          this.dealEl.textContent = (this.dataset.strDealMore || '').replace('[count]', this.dealMin - qty);
        } else {
          this.dealEl.textContent = this.dataset.strDealRest;
        }
      }

      /* ---------- sync in ---------- */

      onForeignCartUpdate(event) {
        if (event?.instanceId === this.instanceId) return;
        const cart = event?.cartData;
        if (cart && Array.isArray(cart.items)) {
          this.paintFromCart(cart);
        } else {
          // Publisher sent a partial shape (add.js response etc.) — one shared refetch.
          this.syncFromServer();
        }
      }

      /*
        H03-01 (audit 2026-10-14). A /cart.js read that was started before one of our writes was
        answered can carry the cart from BEFORE that write — and adopting it repainted the line,
        and serverQty, with the old number, so the next absolute write regressed the line
        (harness: a GET out across a "+" answered after it, then "+" again: 4, 4 instead of 4, 5).
        A read is used only if it started after our last write was answered and no write of ours
        is on the wire when it lands; otherwise our own response is the truth about our line and
        the read is dropped — and, for a card that is waiting on truth, made again once the write
        settles (`write`'s finally).
      */
      syncFromServer() {
        const read = fetchCart(this.settledAt);
        let usable = true;
        let landed = false;
        read
          .then((cart) => {
            if (this.inFlight !== null || this.settledAt > read.startedAt) {
              usable = false;
              return;
            }
            landed = true;
            this.paintFromCart(cart);
          })
          .catch(() => {})
          .finally(() => {
            /* Cleared on failure too. A card that can never be verified because /cart.js is
               unreachable must go back to being usable — the write it then makes will fail the
               same way and surface a real error, which is better than a permanently dead card. */
            if (!this.unverified || !usable) return;
            this.unverified = false;
            const intent = this.pendingIntent;
            this.pendingIntent = null;
            if (!landed && this.heldFrom != null && document.activeElement !== this.input) this.input.value = this.heldFrom;
            this.heldFrom = null;
            if (intent) this.applyPendingIntent(intent);
          });
      }

      /* ---------- status + errors ---------- */

      announceSoon() {
        clearTimeout(this.announceTimer);
        this.announceTimer = setTimeout(() => {
          const qty = this.uiQty;
          this.statusEl.textContent =
            this.dataset.state === 'button' || qty === 0
              ? this.dataset.strRemoved
              : (this.dataset.strInCart || '').replace('[count]', qty);
        }, ANNOUNCE_MS);
      }

      /* S06-015 (audit 2026-10-14). The error paragraph is not a live region, so a failed write
         was shown and never said. The message also goes into the role=status region, which
         exists from first render, and a queued "N in cart" is cancelled so it cannot talk over it. */
      showError(message) {
        const text = message || this.dataset.strError || '';
        clearTimeout(this.announceTimer);
        this.statusEl.textContent = text;
        if (!this.errorEl) return;
        this.errorEl.textContent = text;
        this.errorEl.hidden = false;
      }

      hideError() {
        if (this.errorEl && !this.errorEl.hidden) this.errorEl.hidden = true;
      }

      renderBadge(parsedState) {
        const html = parsedState.sections?.['cart-icon-bubble'];
        const bubble = document.getElementById('cart-icon-bubble');
        const section = html && new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
        if (section && bubble) bubble.innerHTML = section.innerHTML;
      }
    }
  );
})();
